import { Component, inject, OnInit } from '@angular/core';
import { MapService } from '../../services/map/map.service';
import { IvvaStore } from '../../store/store.model';
import { Store } from '@ngrx/store';
import { BehaviorSubject, debounceTime, filter, take, takeUntil } from 'rxjs';
import { Offer } from '../../services/offers/offer.model';
import { CoreModule, DestroyableComponent, formatPrice, OffersType } from '@toevla-webapplications/core';
import { OfferCardComponent } from '../../pages/home/components/offer-card/offer-card.component';
import { TranslateService } from '@ngx-translate/core';
import { OffersMap } from '../../store/offers/offers.model';
import OFFERS_ACTIONS from '../../store/offers/offers.actions';
import { User } from '../../services/user/user.model';

type MarkerItem = {
	lat: number;
	lon: number;
	offers: Offer[];
	marker: any;
};

@Component({
	selector: 'ivva-map',
	templateUrl: './map.component.html',
	styleUrls: ['./map.component.scss'],
	imports: [OfferCardComponent, CoreModule],
})
export class MapComponent extends DestroyableComponent implements OnInit {
	activeMarkerItem: MarkerItem | null;
	activeOffer: Offer | null;
	items: MarkerItem[] = [];
	mapView = new BehaviorSubject<OffersMap | null>(null);
	user: User | null;

	private map: google.maps.Map;

	private readonly mapService = inject(MapService);
	private readonly translate = inject(TranslateService);
	private readonly store = inject(Store<IvvaStore>);

	ngOnInit() {
		this.store
			.select('offers')
			.pipe(take(1))
			.subscribe(({ map, user }) => {
				this.user = user;

				this.mapView.next(map);

				this.mapService.create('map', map).then((map) => {
					this.map = map;

					// NOTE: Will also trigger on zoom
					this.map.addListener('center_changed', () => {
						const zoom = this.map.getZoom();
						const center = this.map.getCenter();

						if (zoom && center) {
							this.mapView.next({ lat: center.lat(), long: center.lng(), zoom });
						}
					});
				});

				this.showOffers();
			});

		this.mapView
			.pipe(
				takeUntil(this.destroyed$),
				debounceTime(500),
				filter((mv) => !!mv)
			)
			.subscribe((options) => this.store.next(OFFERS_ACTIONS.setMap({ options })));
	}

	close() {
		this.activeMarkerItem?.marker.content.classList.remove('tvl-gm-marker--active');
		Array.from(this.activeMarkerItem?.marker.content.children || []).forEach((child: any) => {
			child.classList.remove('tvl-gm-marker__price--active');
		});

		this.activeMarkerItem = null;
		this.activeOffer = null;
	}

	private showOffers() {
		this.store.select('offers', 'data', 'list').subscribe((offers) => {
			this.resetMarkers();
			this.setMarkers(offers);
		});
	}

	private createMarkerItem(mi: MarkerItem): any {
		const priceTag = this.createMarker(mi.offers);

		const marker = this.mapService.createAdvancedMarker(this.map, mi.lat, mi.lon, priceTag);

		marker.addListener('gmp-click', (event: any) => {
			const target = event.explicitOriginalTarget;

			if (target.classList.contains('tvl-gm-marker__price')) {
				const id = target.id;
				this.close();

				if (this.activeOffer?.id !== id) {
					target.classList.add('tvl-gm-marker__price--active');
					target.parentElement.classList.add('tvl-gm-marker--active');

					this.activeMarkerItem =
						this.items.find((item) => {
							const offer = item.offers.find((offer) => offer.id === id);

							if (offer) {
								this.activeOffer = offer;
							}

							return !!offer;
						}) || null;
				}
			}
		});

		return marker;
	}

	private createMarker(offers: Offer[]): HTMLElement {
		const marker = document.createElement('div');
		marker.classList.add('tvl-gm-marker');

		offers.forEach((offer) => {
			const priceTag = this.createPriceTag(offer);
			marker.appendChild(priceTag);
		});

		return marker;
	}

	private createPriceTag(offer: Offer): HTMLElement {
		const priceTag = document.createElement('div');

		priceTag.id = offer.id;
		priceTag.classList.add('tvl-gm-marker__price');

		if (offer.type === OffersType.DAYTRIP && offer.price === undefined) {
			priceTag.textContent = '€ -,--';
		} else if (offer.type === OffersType.ACCOMMODATION && offer.pricePeakseason === undefined) {
			priceTag.textContent = '€ -,--';
		} else {
			const price = formatPrice(offer.price || offer.pricePeakseason).replace('&euro;', '€');
			priceTag.textContent = this.user?.eligibleForDiscount || this.user?.socialOrg ? price : offer.pricePublished ? price : '€ -,--';
		}

		const popup = this.createPopup(offer);
		priceTag.appendChild(popup);

		return priceTag;
	}

	private createPopup(offer: Offer): HTMLElement {
		const popup = document.createElement('div');
		popup.classList.add('tvl-gm-marker__popup');

		const image = document.createElement('div');
		image.classList.add('tvl-gm-marker__popup__image');

		if (offer.poster) {
			image.style.backgroundImage = `url('${offer.poster.url}')`;
		}

		const c = document.createElement('div');
		c.classList.add('flex-column');
		c.classList.add('flex--1');

		const title = document.createElement('p');
		title.classList.add('tvl-gm-marker__popup__title');
		title.textContent = offer.name;

		const label = document.createElement('p');
		label.classList.add('tvl-gm-marker__popup__label');
		label.textContent = this.translate.instant(`PRODUCT_TYPE.${offer.subtype}`);

		c.appendChild(title);
		c.appendChild(label);

		popup.appendChild(image);
		popup.appendChild(c);

		return popup;
	}

	private resetMarkers() {
		this.items.forEach((mi) => {
			mi.marker.map = null;
		});
		this.items = [];
	}

	private setMarkers(offers: Offer[]) {
		const items: MarkerItem[] = [];

		offers.forEach((offer) => {
			const index = items.findIndex((mi) => mi.lat === offer.location.lat && mi.lon === offer.location.lon);

			if (index >= 0) {
				items[index].offers.push(offer);
			} else {
				items.push({
					lat: offer.location.lat,
					lon: offer.location.lon,
					offers: [offer],
					marker: null,
				});
			}
		});

		this.items = items.map((mi) => {
			return {
				...mi,
				marker: this.createMarkerItem(mi),
			};
		});
	}
}
